export type DraftBlock = { kind: "paragraph" | "h2" | "h3"; text: string } | { kind: "list" | "ordered"; items: string[] } | { kind: "table"; rows: string[][] } | { kind: "faq" };
export type BlogDraft = { path: string; h1: string; category: string; breadcrumb: string; readTime: string; datePublished: string; faqHeading: string; faqs: { q: string; a: string }[]; blocks: DraftBlock[] };
export const blogDrafts: Record<"evaluation" | "cost", BlogDraft> = {
  "evaluation": {
    "path": "/what-is-substance-abuse-evaluation-nj",
    "h1": "What Is a Substance Abuse Evaluation in NJ? Questions, Timeline and What Happens Next",
    "category": "Evaluations & Assessments",
    "breadcrumb": "Substance Abuse Evaluations",
    "readTime": "7 min read",
    "datePublished": "2026-10-14",
    "faqHeading": "Frequently asked questions about substance abuse evaluations in NJ",
    "faqs": [
      {
        "q": "What is a substance abuse evaluation in New Jersey?",
        "a": "It's a clinical assessment by a licensed professional that looks at your substance use and its effects on your life, then makes a written recommendation about whether treatment or education is needed and at what level. Courts, employers, probation officers and licensing boards commonly require one."
      },
      {
        "q": "What questions are asked in a drug and alcohol evaluation?",
        "a": "Expect questions about what you use and how often, attempts to cut back, consequences at work, home or with the law, tolerance and withdrawal, mental health, family history and past treatment. Most evaluators also use short standardized questionnaires."
      },
      {
        "q": "Can you fail a substance abuse evaluation?",
        "a": "No. There's no pass or fail. The evaluation produces a clinical finding and a recommendation, which can range from no treatment needed, to education, to outpatient or intensive outpatient treatment."
      },
      {
        "q": "How long does a substance abuse evaluation take?",
        "a": "The appointment usually takes about one to two hours, including the interview and questionnaires. The written report is typically ready within a few business days."
      },
      {
        "q": "Does insurance cover a drug and alcohol evaluation?",
        "a": "Many commercial plans cover clinical evaluations under behavioral health benefits, but coverage varies by plan. Cherry Hill Recovery Center verifies your coverage before your appointment so you know your cost up front."
      },
      {
        "q": "Do I have to get treatment where I was evaluated?",
        "a": "No. The evaluation and the treatment decision are separate. You can take your report to any qualified provider. If you'd like to continue at Cherry Hill Recovery Center, IOP, PHP and Virtual IOP are available on-site with no new intake needed."
      }
    ],
    "blocks": [
      {
        "kind": "paragraph",
        "text": "If a judge, probation officer, employer or licensing board just told you to \"get a substance abuse evaluation,\" you probably have a lot of questions and not many answers. What is it? Who does it? Is it a test you can fail? Will what you say be used against you? This guide answers those questions in plain language, so you can walk into your appointment knowing what to expect."
      },
      {
        "kind": "h2",
        "text": "What a substance abuse evaluation is"
      },
      {
        "kind": "paragraph",
        "text": "A substance abuse evaluation, also called a drug and alcohol evaluation or a substance use assessment, is a structured clinical assessment done by a licensed professional. It has two jobs:"
      },
      {
        "kind": "ordered",
        "items": [
          "Describe your substance use accurately: what you use, how much, how often, and how it affects your life.",
          "Make a recommendation: whether any treatment or education is needed and, if so, what level of care fits."
        ]
      },
      {
        "kind": "paragraph",
        "text": "It's not a drug test, a lecture or a legal judgment. It's a clinical conversation backed by standardized questionnaires, and it ends with a written report."
      },
      {
        "kind": "h2",
        "text": "Why people in New Jersey get evaluated"
      },
      {
        "kind": "paragraph",
        "text": "Most evaluations are requested by someone else. The most common reasons are:"
      },
      {
        "kind": "list",
        "items": [
          "Court cases. Drug possession charges, domestic matters and other cases where substance use may be a factor.",
          "Probation or parole. Evaluation and follow-up are often conditions of supervision.",
          "Employers and EAPs. Often required before returning to work after a substance-related incident.",
          "Family court and child protective services. Part of custody, reunification or case review.",
          "Professional licensing boards. Healthcare workers, attorneys, educators and other licensed professionals.",
          "Your own decision. Some people simply want an honest, professional opinion about their drinking or drug use."
        ]
      },
      {
        "kind": "paragraph",
        "text": "A note on DWI cases. In New Jersey, a DWI conviction usually comes with a required program through the state's Intoxicated Driver Resource Center (IDRC), which runs its own screening. If you're dealing with a DWI, ask your attorney or the IDRC exactly what kind of evaluation is required and whether the evaluator has to be on an approved list before you schedule anywhere, including with us."
      },
      {
        "kind": "h2",
        "text": "What questions they ask"
      },
      {
        "kind": "paragraph",
        "text": "The clinical interview is a conversation, not an interrogation. Expect questions in these areas:"
      },
      {
        "kind": "list",
        "items": [
          "Your substance use history. What you've used, when you started, how much and how often, and when you last used.",
          "Patterns and control. Whether you've tried to cut back, use more than you intended, or spend a lot of time getting, using or recovering.",
          "Consequences. Problems at work, school or home, legal issues, health effects, and risky situations like driving after drinking.",
          "Physical dependence. Tolerance (needing more for the same effect) and withdrawal symptoms when you stop.",
          "Mental health. Depression, anxiety, trauma, sleep, and any past diagnoses or medications.",
          "Family and personal history. Substance use in your family, your living situation, relationships and support system.",
          "Past treatment. Previous counseling, rehab, detox or recovery meetings, and what helped."
        ]
      },
      {
        "kind": "paragraph",
        "text": "Clinicians typically pair the interview with standardized screening tools. Common ones include the AUDIT for alcohol and the DAST for drugs. They also use the DSM-5 criteria for substance use disorder, which rate it as mild, moderate or severe based on how many of 11 criteria apply. To match a recommendation to the right intensity of care, many clinicians use the ASAM Criteria, the national standard for level-of-care decisions."
      },
      {
        "kind": "h2",
        "text": "Can you \"fail\" a substance abuse evaluation?"
      },
      {
        "kind": "paragraph",
        "text": "No. There's no pass or fail. The result is a clinical finding and a recommendation. Possible outcomes include:"
      },
      {
        "kind": "list",
        "items": [
          "No treatment needed",
          "Education or early intervention",
          "Outpatient counseling",
          "[An Intensive Outpatient Program (IOP)](/iop-cherry-hill-nj)",
          "[A Partial Hospitalization Program (PHP)](/php-cherry-hill-nj)",
          "[Detox referral](/detox-referral-coordination-nj) first, if you're physically dependent on alcohol, opioids or benzodiazepines"
        ]
      },
      {
        "kind": "paragraph",
        "text": "Some courts and employers also require a separate drug screen. Ask your referral source whether yours does."
      },
      {
        "kind": "h2",
        "text": "Why honesty works in your favor"
      },
      {
        "kind": "paragraph",
        "text": "The most common worry we hear is \"If I'm honest, it'll be used against me.\" In practice, the opposite is usually true. Evaluators are trained to notice when someone is minimizing, and minimizing becomes part of the clinical picture. An honest evaluation produces an accurate report, and an accurate report holds up best with courts, employers and licensing boards. It also makes it far more likely that any recommendation actually fits your life."
      },
      {
        "kind": "h2",
        "text": "How long it takes"
      },
      {
        "kind": "table",
        "rows": [
          [
            "Step",
            "Typical timing"
          ],
          [
            "Scheduling",
            "Usually within a few business days, sooner when there's a court or employer deadline"
          ],
          [
            "Paperwork",
            "15–30 minutes, often completed before the appointment"
          ],
          [
            "Interview and assessment tools",
            "About 1–2 hours in total"
          ],
          [
            "Written report",
            "Usually within a few business days after the appointment"
          ]
        ]
      },
      {
        "kind": "paragraph",
        "text": "If you have a deadline, say so when you call. At Cherry Hill Recovery Center, we work backward from the date your report is due."
      },
      {
        "kind": "h2",
        "text": "What to bring"
      },
      {
        "kind": "list",
        "items": [
          "A valid photo ID",
          "Your insurance card",
          "The paperwork from the court, employer or agency, especially anything that lists their specific requirements",
          "Any prior treatment records or relevant medical records, if you have them",
          "A list of current medications"
        ]
      },
      {
        "kind": "h2",
        "text": "Can it be done online?"
      },
      {
        "kind": "paragraph",
        "text": "Often, yes. Evaluations can be done by secure video anywhere in New Jersey. Some courts, employers and licensing boards require an in-person assessment, though, so confirm which format your referral source accepts before you book."
      },
      {
        "kind": "h2",
        "text": "Who sees the results?"
      },
      {
        "kind": "paragraph",
        "text": "Your evaluation is confidential. Substance use treatment records have extra federal protection under 42 CFR Part 2, on top of HIPAA. The report goes to you and only goes to the court, employer or agency if you sign a written release. Before the evaluation starts, your clinician should explain exactly what will be shared and with whom."
      },
      {
        "kind": "h2",
        "text": "What happens after the evaluation"
      },
      {
        "kind": "paragraph",
        "text": "If no treatment is recommended, you take your report to whoever requested it and you're done."
      },
      {
        "kind": "paragraph",
        "text": "If treatment is recommended, the report will say what level of care fits. You're free to take that recommendation to any qualified provider. One advantage of getting evaluated at a treatment center is that you don't have to start over somewhere new. At Cherry Hill Recovery Center, [IOP](/iop-cherry-hill-nj), [Evening IOP](/evening-iop-new-jersey), [PHP](/php-cherry-hill-nj) and [statewide Virtual IOP](/virtual-iop-new-jersey) are all available, and our admissions team [verifies your insurance](/verify-insurance) before anything starts."
      },
      {
        "kind": "paragraph",
        "text": "Many people also benefit from a [psychiatric evaluation](/psychiatric-evaluation-nj). A substance use evaluation looks at drinking and drug use. A psychiatric evaluation looks at mental health diagnoses and medication. Depression, anxiety and substance use often show up together, and we can do both on-site."
      },
      {
        "kind": "faq"
      },
      {
        "kind": "h2",
        "text": "Get your evaluation scheduled"
      },
      {
        "kind": "paragraph",
        "text": "Not sure what level of care fits? Explore our [level of care quiz](/find-your-level-of-care) as a starting point; it does not replace a clinical evaluation."
      },
      {
        "kind": "paragraph",
        "text": "If you need an evaluation, especially with a deadline, the first step is a short phone call. We'll confirm whether our evaluation meets your court's, employer's or board's requirements before you book, so you don't waste time or money."
      },
      {
        "kind": "paragraph",
        "text": "Learn more about our [drug and alcohol evaluation in Cherry Hill](/drug-alcohol-evaluation-nj), or call 856-200-3127. It's confidential, and admissions is available 24/7. Cherry Hill Recovery Center is at 1930 Marlton Pike East, Suite U100, Cherry Hill, NJ 08003. We're Joint Commission accredited and licensed by NJ DMHAS (#2000881)."
      }
    ]
  },
  "cost": {
    "path": "/iop-php-cost-nj",
    "h1": "How Much Does IOP or PHP Cost in NJ? What You'll Actually Pay With (and Without) Insurance",
    "category": "Insurance & Costs",
    "breadcrumb": "IOP & PHP Cost",
    "readTime": "8 min read",
    "datePublished": "2026-10-14",
    "faqHeading": "Frequently asked questions about IOP and PHP cost in New Jersey",
    "faqs": [
      {
        "q": "How much does IOP cost in New Jersey?",
        "a": "It depends mostly on your insurance. With in-network commercial coverage, most people pay their deductible plus a daily copay or coinsurance, capped by their plan's annual out-of-pocket maximum. Our free insurance verification shows your likely cost before you start."
      },
      {
        "q": "Is PHP more expensive than IOP?",
        "a": "Per day, yes, because PHP includes 5 to 8 hours of care, 5 to 6 days a week. PHP stays are shorter, though, usually 2 to 4 weeks, and many people step down to IOP afterward. Both cost much less than residential rehab."
      },
      {
        "q": "Does insurance cover IOP and PHP in NJ?",
        "a": "Most commercial plans do. Federal parity law requires plans that cover addiction treatment to cover it on terms no more restrictive than medical care, and ACA plans must include substance use treatment. Coverage details still vary by plan, so verify before you start."
      },
      {
        "q": "Does Cherry Hill Recovery Center accept Medicaid or NJ FamilyCare?",
        "a": "Not currently. We work with most major commercial plans, including Horizon BCBSNJ, Aetna, Cigna, UnitedHealthcare, Optum, UMR, Anthem, Carelon and Harvard Pilgrim. If you have Medicaid or NJ FamilyCare, ReachNJ at 1-844-732-2465 can help you find a program that accepts it."
      },
      {
        "q": "Will my employer find out if I use insurance for rehab?",
        "a": "No. Your treatment is protected under HIPAA and 42 CFR Part 2, the federal law for substance use treatment records. Employers don't see your claims. With Evening IOP or Virtual IOP, many people don't need to take time off at all."
      },
      {
        "q": "How can I find out exactly what I'll pay?",
        "a": "Use our online insurance verification, which takes about 60 seconds, or call 856-200-3127. An admissions specialist will confirm your deductible, copay and out-of-pocket status before you commit to anything."
      }
    ],
    "blocks": [
      {
        "kind": "paragraph",
        "text": "\"How much is this going to cost?\" is usually the second question people ask us, right after \"Can you help?\" It's a fair question, and it's often the reason people put off treatment for months. The honest answer is that what you pay for an Intensive Outpatient Program (IOP) or Partial Hospitalization Program (PHP) in New Jersey depends far more on your insurance plan than on the program itself. This guide explains what drives the cost, how insurance math works for outpatient rehab, and how to get your real number in a few minutes instead of guessing."
      },
      {
        "kind": "h2",
        "text": "The short answer"
      },
      {
        "kind": "paragraph",
        "text": "For most people with commercial insurance, the cost of IOP or PHP comes down to three numbers on your plan:"
      },
      {
        "kind": "list",
        "items": [
          "Your deductible: how much you pay before the plan starts sharing costs.",
          "Your copay or coinsurance: your share after the deductible is met, either a flat amount per day or a percentage.",
          "Your out-of-pocket maximum: the most you can pay in a plan year for covered, in-network care. After you hit it, covered care is paid at 100%."
        ]
      },
      {
        "kind": "paragraph",
        "text": "That out-of-pocket maximum matters more than anything else. Outpatient treatment involves many sessions, so plenty of people reach their maximum during treatment. Once that happens, the rest of their covered care that year costs nothing."
      },
      {
        "kind": "h2",
        "text": "Why PHP and IOP are priced differently"
      },
      {
        "kind": "paragraph",
        "text": "PHP and IOP are both outpatient programs, which means you sleep at home. The difference is intensity, and intensity is what drives the cost."
      },
      {
        "kind": "table",
        "rows": [
          [
            "",
            "PHP",
            "IOP"
          ],
          [
            "Days per week",
            "5–6",
            "3–4"
          ],
          [
            "Hours per day",
            "5–8",
            "About 3"
          ],
          [
            "Typical length",
            "2–4 weeks",
            "8–12 weeks"
          ],
          [
            "Clinical contact",
            "Daily, with close psychiatric monitoring",
            "Several times a week, with psychiatric oversight as needed"
          ],
          [
            "Billed as",
            "A per-day program rate",
            "A per-day or per-session program rate"
          ]
        ]
      },
      {
        "kind": "paragraph",
        "text": "A PHP day costs more than an IOP day because it includes more hours of care. But a PHP stay is shorter. Many people do a few weeks of [PHP in Cherry Hill](/php-cherry-hill-nj) and then step down to [IOP](/iop-cherry-hill-nj), so the total cost depends on the whole path, not on a single day's rate."
      },
      {
        "kind": "paragraph",
        "text": "Both cost far less than residential rehab. With residential care you're also paying for 24-hour staffing, a bed and meals. If you're weighing the two, our [inpatient vs. outpatient rehab guide](/inpatient-vs-outpatient-rehab-guide-nj) walks through who needs which."
      },
      {
        "kind": "h2",
        "text": "How insurance covers IOP and PHP in New Jersey"
      },
      {
        "kind": "h3",
        "text": "Federal parity law"
      },
      {
        "kind": "paragraph",
        "text": "Under the Mental Health Parity and Addiction Equity Act, a plan that covers addiction treatment can't make that coverage more restrictive than its medical and surgical coverage. That means no tighter visit limits, no higher copays and no stricter approval rules just because the care is for substance use. Plans sold under the Affordable Care Act are also required to include substance use treatment as an essential health benefit."
      },
      {
        "kind": "h3",
        "text": "New Jersey protections"
      },
      {
        "kind": "paragraph",
        "text": "New Jersey goes further than federal law for state-regulated plans and limits how insurers can use prior authorization to delay medically necessary addiction treatment with in-network providers. Whether those rules apply depends on how your plan is set up. Self-funded employer plans follow federal rules instead. You don't need to work this out yourself. Our admissions team checks which rules apply when we verify your benefits."
      },
      {
        "kind": "h3",
        "text": "In-network vs. out-of-network"
      },
      {
        "kind": "paragraph",
        "text": "This is often the biggest swing in what you'll pay. In-network care counts toward your out-of-pocket maximum at your plan's normal rates. Out-of-network care may have a separate, higher deductible, or no coverage at all on HMO and EPO plans. Cherry Hill Recovery Center works with most major commercial plans, including [Horizon BCBSNJ](/insurance/horizon-blue-cross-blue-shield-nj-rehab), [Aetna](/insurance/aetna-rehab-nj), [Cigna](/insurance/cigna-rehab-nj) and [UnitedHealthcare](/insurance/united-healthcare-rehab-nj), plus Optum, UMR, Anthem, Carelon and Harvard Pilgrim."
      },
      {
        "kind": "h3",
        "text": "HMO, PPO and EPO plans"
      },
      {
        "kind": "list",
        "items": [
          "HMO: usually needs a referral from your primary care doctor and in-network care.",
          "PPO: the most flexibility, with lower costs in-network and some out-of-network coverage.",
          "EPO: in-network only, but usually no referral is needed."
        ]
      },
      {
        "kind": "h2",
        "text": "A worked example: what the math looks like"
      },
      {
        "kind": "paragraph",
        "text": "Here's a simplified example with made-up plan numbers, just to show how the pieces fit together. Your plan will be different."
      },
      {
        "kind": "paragraph",
        "text": "Say your plan has a $2,000 deductible, 20% coinsurance and a $6,000 out-of-pocket maximum, and you haven't used any of your deductible yet this year."
      },
      {
        "kind": "ordered",
        "items": [
          "First, you pay toward your deductible. The first $2,000 of covered treatment is yours.",
          "Then you split the cost with your plan. After the deductible, you pay 20% and your plan pays 80%.",
          "Then the cap kicks in. Once your total spending (deductible plus coinsurance) reaches $6,000, your plan pays 100% of covered in-network care for the rest of the plan year."
        ]
      },
      {
        "kind": "paragraph",
        "text": "In this example, the most you could owe for a full course of PHP and IOP, plus any other covered care that year, is $6,000. If you'd already met part of your deductible from doctor visits or prescriptions earlier in the year, you'd owe less. Some plans use a flat daily copay instead of coinsurance, which makes the math even simpler."
      },
      {
        "kind": "paragraph",
        "text": "Timing matters too. If you've already met most of your deductible this year, starting treatment before your plan resets (often January 1) can cost noticeably less than waiting."
      },
      {
        "kind": "h2",
        "text": "Costs people forget to plan for"
      },
      {
        "kind": "paragraph",
        "text": "The program fee isn't the only expense. Ask about these up front:"
      },
      {
        "kind": "list",
        "items": [
          "Medication. If [medication-assisted treatment](/how-much-does-mat-cost-nj) like Suboxone or Sublocade is part of your plan, it's usually billed separately under your pharmacy or medical benefit.",
          "Psychiatric visits. Medication management may be billed as a specialist visit.",
          "Detox. If you need medical detox first, that's a separate stay at a detox facility. We coordinate the referral and hold your spot for the step-down.",
          "Lost income. This one doesn't show up on a bill, but it's real. It's also where outpatient care saves the most. [Evening IOP](/evening-iop-new-jersey) starts after work, and [Virtual IOP](/virtual-iop-new-jersey) is available anywhere in New Jersey, so many people keep working full time.",
          "Transportation and childcare. Virtual sessions can cut both."
        ]
      },
      {
        "kind": "h2",
        "text": "What if I don't have commercial insurance?"
      },
      {
        "kind": "paragraph",
        "text": "Cherry Hill Recovery Center doesn't currently accept Medicaid, NJ FamilyCare or Medicare. If that's your coverage, New Jersey's free, 24/7 addiction help line, ReachNJ (1-844-732-2465), can connect you with programs that do. If you're uninsured or between plans, call us anyway. We'll tell you honestly what your options are, including whether you may qualify for a marketplace plan or a special enrollment period after a job change."
      },
      {
        "kind": "h2",
        "text": "How to get your real number in 60 seconds"
      },
      {
        "kind": "paragraph",
        "text": "You don't have to guess or call five places. [Our online insurance verification](/verify-insurance) checks your benefits in real time and shows which programs your plan likely covers, along with your deductible and out-of-pocket status. It's free, confidential and available 24 hours a day. An admissions specialist then calls you, usually within minutes, to confirm the exact numbers before you commit to anything. Here's [how the 60-second check works](/verify-insurance-60-seconds-nj)."
      },
      {
        "kind": "paragraph",
        "text": "A note on privacy: verifying your insurance doesn't notify your employer. Substance use treatment records are protected under HIPAA and 42 CFR Part 2, and an explanation of benefits won't describe the treatment you received in detail."
      },
      {
        "kind": "paragraph",
        "text": "Not sure whether you need PHP or IOP? Take our [two-minute level of care quiz](/find-your-level-of-care) or call for a free, confidential assessment, often the same day."
      },
      {
        "kind": "faq"
      },
      {
        "kind": "h2",
        "text": "Get your number, then decide"
      },
      {
        "kind": "paragraph",
        "text": "Cost shouldn't be the thing that keeps you or someone you love out of treatment, and it usually turns out to be lower than people fear. Find out what your plan covers in about a minute, then make your decision with real information."
      },
      {
        "kind": "paragraph",
        "text": "[Verify your insurance now](/verify-insurance) or call Cherry Hill Recovery Center at 856-200-3127. Admissions is available 24/7. We're at 1930 Marlton Pike East, Suite U100, in Cherry Hill, serving Camden, Burlington and Gloucester counties."
      }
    ]
  }
};
